// The RBP Shopping List page's data: the header and the group rows, read in parallel on mount and
// on `retry`. A 404 on either read is "this store has no list" (the store does not take part),
// shown as such rather than as a failure; any other failure is an error with a retry. A null header
// is a participant with no current report yet: the page draws its frame with blank values.
//
// `refresh` re-reads both after a selection changes, WITHOUT going back to `loading`, so the rows
// (and the open group's lines under them) stay on screen; a failed refresh keeps what is shown, and
// the next change refreshes again.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  RbpRequestError,
  fetchRbpBenefitGroups,
  fetchRbpShoppingListHeader,
  type RbpBenefitGroupRow,
  type RbpShoppingListHeader,
} from '../api/rbp.client';

export type RbpShoppingListState =
  | { readonly status: 'loading' }
  | { readonly status: 'not-a-participant' }
  | { readonly status: 'error' }
  | {
      readonly status: 'ready';
      readonly header: RbpShoppingListHeader | null;
      readonly groups: readonly RbpBenefitGroupRow[];
    };

export function useRbpShoppingList(): RbpShoppingListState & {
  readonly retry: () => void;
  readonly refresh: () => void;
} {
  const [state, setState] = useState<RbpShoppingListState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const latest = useRef(0);

  useEffect(() => {
    const request = ++latest.current;
    setState({ status: 'loading' });
    void Promise.all([fetchRbpShoppingListHeader(), fetchRbpBenefitGroups()])
      .then(([header, groups]) => {
        if (request === latest.current) setState({ status: 'ready', header, groups });
      })
      .catch((error: unknown) => {
        if (request !== latest.current) return;
        const notAParticipant = error instanceof RbpRequestError && error.status === 404;
        setState({ status: notAParticipant ? 'not-a-participant' : 'error' });
      });
    return () => {
      // A newer read (or an unmount) makes this one's answer stale.
      latest.current += 1;
    };
  }, [attempt]);

  const retry = useCallback(() => setAttempt((count) => count + 1), []);
  const refresh = useCallback(() => {
    const request = ++latest.current;
    void Promise.all([fetchRbpShoppingListHeader(), fetchRbpBenefitGroups()])
      .then(([header, groups]) => {
        if (request === latest.current) setState({ status: 'ready', header, groups });
      })
      .catch(() => {
        // Keep the values on screen: the next change refreshes again.
      });
  }, []);
  return { ...state, retry, refresh };
}
